import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdChatBubbleOutline, MdClose, MdSend, MdKeyboardArrowDown, MdChevronLeft, MdDesktopWindows, MdSupportAgent } from 'react-icons/md';
import { cn } from '../../lib/utils';
import { apiFetchUrl, resolveWebSocketUrl } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import {
  getChatRemoteStatus,
  requestSupportSessionFromChat,
  SupportSession,
} from '../../services/supportApi';
import { canInitiateRemoteSupport } from '../../utils/remoteSupportAccess';
import { useEngineerRemoteSupport } from '../../context/EngineerRemoteSupportProvider';

// ─── Cell-phone style ringtone ────────────────────────────────────────────────
let ringInterval: ReturnType<typeof setInterval> | null = null;

const playRing = () => {
    try {
        const Ctx = window.AudioContext || (window as any).webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const ringTone = (startT: number) => {
            [480, 620].forEach(freq => {
                const osc = ctx.createOscillator();
                const g   = ctx.createGain();
                osc.type = 'sine'; osc.frequency.value = freq;
                osc.connect(g); g.connect(ctx.destination);
                g.gain.setValueAtTime(0,    startT);
                g.gain.linearRampToValueAtTime(0.12, startT + 0.01);
                g.gain.setValueAtTime(0.12, startT + 0.39);
                g.gain.linearRampToValueAtTime(0,    startT + 0.40);
                osc.start(startT); osc.stop(startT + 0.41);
            });
        };
        ringTone(ctx.currentTime + 0.0);
        ringTone(ctx.currentTime + 0.5);
        setTimeout(() => ctx.close().catch(() => {}), 1200);
    } catch { /* ignore */ }
};

const startRing = () => { if (ringInterval) return; playRing(); ringInterval = setInterval(playRing, 3000); };
const stopRing  = () => { if (ringInterval) { clearInterval(ringInterval); ringInterval = null; } };

const syncRingtone = (sessions: Array<{ status: string }>) => {
    const hasWaiting = sessions.some(s => s.status === 'waiting');
    if (hasWaiting) startRing();
    else stopRing();
};

const playNotif = playRing; // one-shot for message notifications

const getWsUrl = (token: string) =>
    resolveWebSocketUrl(apiFetchUrl(`/ws/chat?token=${encodeURIComponent(token)}`));

interface Session {
    sessionId: string;
    clientName: string;
    clientEmail: string;
    clientPortal: string;
    targetAgentPortal: 'admin' | 'tech';
    agentName: string | null;
    agentPortal: string | null;
    status: 'waiting' | 'active' | 'closed';
    messages: Array<{ sender: string; senderName: string; text: string; ts: string }>;
    createdAt: string;
    alreadyHandled?: boolean;
}

const appendUniqueMessage = (messages: Session['messages'] | undefined, incoming: Session['messages'][number]) => {
    const list = messages ?? [];
    const exists = list.some(
        (m) =>
            m.ts === incoming.ts &&
            m.sender === incoming.sender &&
            m.senderName === incoming.senderName &&
            m.text === incoming.text
    );
    return exists ? list : [...list, incoming];
};

const sessionInitial = (name?: string, email?: string) =>
    (name?.trim()?.[0] || email?.trim()?.[0] || '?').toUpperCase();

const normalizeSession = (raw: Partial<Session> & { sessionId?: string; _id?: string }): Session => ({
    sessionId: String(raw.sessionId || raw._id || ''),
    clientName: raw.clientName || (raw as { customerName?: string }).customerName || raw.clientEmail || (raw as { customerEmail?: string }).customerEmail || 'Guest',
    clientEmail: raw.clientEmail || (raw as { customerEmail?: string }).customerEmail || '',
    clientPortal: raw.clientPortal || (raw as { portalType?: string }).portalType || 'customer',
    targetAgentPortal: raw.targetAgentPortal === 'admin' ? 'admin' : 'tech',
    agentName: raw.agentName ?? null,
    agentPortal: raw.agentPortal ?? null,
    status: raw.status === 'active' || raw.status === 'closed' ? raw.status : 'waiting',
    messages: Array.isArray(raw.messages) ? raw.messages : [],
    createdAt: raw.createdAt || new Date().toISOString(),
    alreadyHandled: raw.alreadyHandled,
});

export function SupportAgentWidget() {
    const { user, permissions } = useAuth();
    const navigate = useNavigate();

    const [open, setOpen]            = useState(false);
    const [sessions, setSessions]    = useState<Session[]>([]);
    const [activeSession, setActive] = useState<Session | null>(null);
    const [input, setInput]          = useState('');
    const [wsReady, setWsReady]      = useState(false);
    const [unread, setUnread]        = useState(0);
    const [remoteStatus, setRemoteStatus] = useState<{
        customerOnline: boolean;
        canRequestRemote: boolean;
        activeRemoteSessionId: string | null;
        remoteSessionStatus: string | null;
    } | null>(null);
    const [remoteLoading, setRemoteLoading] = useState(false);
    const [remoteError, setRemoteError] = useState<string | null>(null);
    const [pendingRemoteSession, setPendingRemoteSession] = useState<SupportSession | null>(null);
    const [showRemoteModal, setShowRemoteModal] = useState(false);
    const [remotePurpose, setRemotePurpose] = useState('');
    const [remoteDuration, setRemoteDuration] = useState(20);
    const [remoteMode, setRemoteMode] = useState<'view' | 'control'>('view');

    const canManageRemote = canInitiateRemoteSupport(user?.role, permissions ?? []);
    const {
        session: engineerRemoteSession,
        phase: engineerRemotePhase,
        attachSession: attachEngineerRemoteSession,
        loadSession: loadEngineerRemoteSession,
    } = useEngineerRemoteSupport();

    const wsRef        = useRef<WebSocket | null>(null);
    const activeRef    = useRef<Session | null>(null);
    const endRef       = useRef<HTMLDivElement>(null);
    const aliveRef     = useRef(true);
    const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => { activeRef.current = activeSession; }, [activeSession]);
    useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [activeSession?.messages]);

    const wsSend = useCallback((data: object) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify(data));
    }, []);

    const fetchRemoteStatus = useCallback(async (sessionId: string) => {
        try {
            const status = await getChatRemoteStatus(sessionId);
            setRemoteStatus(status);
        } catch {
            setRemoteStatus(null);
        }
    }, []);

    useEffect(() => {
        if (activeSession?.sessionId) {
            void fetchRemoteStatus(activeSession.sessionId);
        } else {
            setRemoteStatus(null);
            setPendingRemoteSession(null);
            setRemoteError(null);
        }
    }, [activeSession?.sessionId, fetchRemoteStatus]);

    const userId = user?._id;

    useEffect(() => {
        if (!userId || !user) return;
        aliveRef.current = true;

        const connect = () => {
            if (!aliveRef.current) return;
            const token = localStorage.getItem('accessToken');
            if (!token) return;
            const ws = new WebSocket(getWsUrl(token));
            wsRef.current = ws;

            ws.onopen = () => { console.log('[SupportAgent] Tech WS open'); setWsReady(true); };

            ws.onmessage = (e) => {
                try {
                    const msg = JSON.parse(e.data);
                    console.log('[SupportAgent] Tech recv:', msg.type);

                    if (msg.type === 'ack') return;

                    if (msg.type === 'error') {
                        const errMsg = msg.payload?.message as string | undefined;
                        if (errMsg && activeRef.current) {
                            setRemoteError(errMsg);
                        }
                        return;
                    }

                    if (msg.type === 'session_list') {
                        const list = (Array.isArray(msg.payload) ? msg.payload : [])
                            .map(normalizeSession)
                            .filter(s => s.status !== 'closed' && s.targetAgentPortal === 'tech');
                        setSessions(list);
                        syncRingtone(list);
                        return;
                    }
                    if (msg.type === 'new_session') {
                        const s = normalizeSession(msg.payload as Partial<Session>);
                        if (s.targetAgentPortal !== 'tech') return;
                        setSessions(prev => {
                            if (prev.find(x => x.sessionId === s.sessionId)) return prev;
                            const next = [s, ...prev];
                            syncRingtone(next);
                            return next;
                        });
                        setUnread(n => n + 1);
                        if (!open) setOpen(true);
                        return;
                    }
                    if (msg.type === 'session_updated') {
                        const s = normalizeSession(msg.payload as Partial<Session>);
                        if (s.targetAgentPortal !== 'tech') return;
                        setSessions(prev => {
                            const next = prev.map(x => x.sessionId === s.sessionId ? s : x).filter(x => x.status !== 'closed');
                            syncRingtone(next);
                            return next;
                        });
                        if (activeRef.current?.sessionId === s.sessionId) setActive(s);
                        return;
                    }
                    if (msg.type === 'session_state') {
                        const s = normalizeSession(msg.payload as Partial<Session>);
                        setSessions(prev => {
                            const existing = prev.find(x => x.sessionId === s.sessionId);
                            const next = existing
                                ? prev.map(x => x.sessionId === s.sessionId ? s : x)
                                : [s, ...prev];
                            syncRingtone(next);
                            return next;
                        });
                        setActive(s);
                        return;
                    }
                    if (msg.type === 'chat_message') {
                        const { sessionId, msg: m } = msg.payload;
                        setSessions(prev => prev.map(s => s.sessionId !== sessionId ? s : { ...s, messages: appendUniqueMessage(s.messages, m) }));
                        if (activeRef.current?.sessionId === sessionId) {
                            setActive(prev => prev ? { ...prev, messages: appendUniqueMessage(prev.messages, m) } : prev);
                        } else { setUnread(n => n + 1); playNotif(); }
                        return;
                    }
                    if (msg.type === 'session_message_preview') {
                        const { sessionId, msg: m } = msg.payload;
                        setSessions(prev => prev.map(s => s.sessionId !== sessionId ? s : { ...s, messages: appendUniqueMessage(s.messages, m) }));
                        setUnread(n => n + 1); playNotif();
                        return;
                    }
                    if (msg.type === 'session_closed') {
                        const { sessionId } = msg.payload;
                        setSessions(prev => {
                            const next = prev.filter(s => s.sessionId !== sessionId);
                            syncRingtone(next);
                            return next;
                        });
                        if (activeRef.current?.sessionId === sessionId) setActive(null);
                        return;
                    }
                    if (
                        msg.type === 'remote-support-request' ||
                        msg.type === 'remote-support-approved' ||
                        msg.type === 'remote-support-rejected' ||
                        msg.type === 'remote-session-started' ||
                        msg.type === 'remote-session-ended'
                    ) {
                        const chatSessionId = msg.payload?.chatSessionId as string | undefined;
                        if (chatSessionId && activeRef.current?.sessionId === chatSessionId) {
                            if (msg.type === 'remote-support-request' && msg.payload?.session) {
                                setPendingRemoteSession(msg.payload.session as SupportSession);
                            }
                            if (
                                msg.type === 'remote-support-rejected' ||
                                msg.type === 'remote-session-ended'
                            ) {
                                setPendingRemoteSession(null);
                            }
                            const m = msg.payload?.message as Session['messages'][number] | undefined;
                            if (m) {
                                setSessions(prev =>
                                    prev.map(s =>
                                        s.sessionId !== chatSessionId
                                            ? s
                                            : { ...s, messages: appendUniqueMessage(s.messages, m) }
                                    )
                                );
                                setActive(prev =>
                                    prev && prev.sessionId === chatSessionId
                                        ? { ...prev, messages: appendUniqueMessage(prev.messages, m) }
                                        : prev
                                );
                            }
                            void fetchRemoteStatus(chatSessionId);
                        }
                        return;
                    }
                } catch (err) { console.error('[SupportAgent] Tech parse error:', err); }
            };

            ws.onclose = (ev) => {
                console.warn('[SupportAgent] Tech WS closed:', ev.code);
                setWsReady(false); wsRef.current = null;
                if (aliveRef.current) reconnectRef.current = setTimeout(connect, 3000);
            };
            ws.onerror = (err) => console.error('[SupportAgent] Tech WS error:', err);
        };

        connect();
        return () => {
            aliveRef.current = false;
            if (reconnectRef.current) clearTimeout(reconnectRef.current);
            stopRing();
            wsRef.current?.close(); wsRef.current = null;
        };
    }, [userId, fetchRemoteStatus]); // eslint-disable-line react-hooks/exhaustive-deps

    const joinSession = (s: Session) => { stopRing(); wsSend({ type: 'agent_join', payload: { sessionId: s.sessionId } }); };
    const sendMsg = () => {
        const text = input.trim();
        if (!text || !activeSession) return;
        setInput('');
        // Do NOT add optimistically — the server echoes it back via chat_message
        wsSend({ type: 'chat_message', payload: { sessionId: activeSession.sessionId, text } });
    };
    const closeSession = () => {
        if (!activeSession) return;
        wsSend({ type: 'chat_close', payload: { sessionId: activeSession.sessionId } });
        setActive(null);
    };
    const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMsg(); }
    };

    const handleRequestRemote = async () => {
        if (!activeSession || !canManageRemote) return;
        setRemoteError(null);
        setRemoteLoading(true);
        try {
            const session = await requestSupportSessionFromChat({
                chatSessionId: activeSession.sessionId,
                mode: remoteMode,
                purpose: remotePurpose.trim() || 'Assist via support chat',
                reason: remotePurpose.trim(),
                estimatedDurationMinutes: remoteDuration,
            });
            setPendingRemoteSession(session);
            attachEngineerRemoteSession(session);
            setShowRemoteModal(false);
            void fetchRemoteStatus(activeSession.sessionId);
        } catch (err: unknown) {
            setRemoteError(err instanceof Error ? err.message : 'Failed to request remote support');
        } finally {
            setRemoteLoading(false);
        }
    };

    const openRemoteWorkspace = () => {
        const sessionId =
            engineerRemoteSession?.sessionId ||
            remoteStatus?.activeRemoteSessionId ||
            pendingRemoteSession?.sessionId;
        if (!sessionId) return;
        void loadEngineerRemoteSession(sessionId);
        navigate(`/support/remote?sessionId=${sessionId}&t=${Date.now()}`);
    };

    const remoteWaiting =
        engineerRemotePhase === 'waiting' ||
        pendingRemoteSession?.status === 'pending' ||
        remoteStatus?.remoteSessionStatus === 'pending';

    const remoteActive = engineerRemotePhase === 'connected' || remoteStatus?.remoteSessionStatus === 'connected';

    const remoteDisabled =
        !activeSession ||
        activeSession.status === 'closed' ||
        activeSession.alreadyHandled ||
        !canManageRemote ||
        !remoteStatus?.customerOnline ||
        !remoteStatus?.canRequestRemote ||
        remoteLoading;

    if (!user) return null;

    return (
        <div className="fixed bottom-4 right-4 lg:bottom-6 lg:right-6 z-[1000] flex flex-col items-end pointer-events-none">
            <div className={cn(
                "bg-[hsl(var(--card))] rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 origin-bottom-right flex flex-col mb-4 border border-[hsl(var(--border))] w-[380px] pointer-events-auto",
                open ? "scale-100 opacity-100 h-[520px]" : "scale-0 opacity-0 pointer-events-none h-0"
            )}>
                <div className="bg-[hsl(var(--card))] border-b border-[hsl(var(--border))] text-[hsl(var(--foreground))] px-4 py-3 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2">
                        {activeSession && <button onClick={() => setActive(null)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mr-1"><MdChevronLeft className="w-5 h-5" /></button>}
                        <span className={cn("w-2 h-2 rounded-full", wsReady ? "bg-green-400 animate-pulse" : "bg-yellow-400")} />
                        <span className="font-bold text-[13px]">
                            {activeSession ? `Chat — ${activeSession.clientName || 'Guest'}` : `Support Inbox (Tech)${sessions.length > 0 ? ` · ${sessions.length}` : ''}`}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {activeSession && canManageRemote && activeSession.status !== 'closed' && !activeSession.alreadyHandled && (
                            <button
                                type="button"
                                disabled={remoteDisabled}
                                onClick={() => setShowRemoteModal(true)}
                                title={
                                    !remoteStatus?.customerOnline
                                        ? 'Customer must be online in their portal'
                                        : !remoteStatus?.canRequestRemote
                                          ? 'Remote session already active'
                                          : 'Request remote desktop access'
                                }
                                className="flex items-center gap-1 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 px-2 py-0.5 text-[10px] font-bold text-white"
                            >
                                <MdDesktopWindows className="w-3.5 h-3.5" />
                                Remote
                            </button>
                        )}
                        {activeSession && (remoteWaiting || remoteActive || remoteStatus?.activeRemoteSessionId || pendingRemoteSession) && (
                            <button
                                type="button"
                                onClick={openRemoteWorkspace}
                                className="flex items-center gap-1 rounded border border-blue-400/50 bg-blue-600/20 px-2 py-0.5 text-[10px] font-bold text-blue-200"
                            >
                                <MdSupportAgent className="w-3.5 h-3.5" />
                                {remoteWaiting ? 'Waiting…' : 'Open Workspace'}
                            </button>
                        )}
                        {activeSession && activeSession.status !== 'closed' && !activeSession.alreadyHandled && (
                            <button onClick={closeSession} className="text-xs bg-red-500 hover:bg-red-600 px-2 py-0.5 rounded font-bold">End</button>
                        )}
                        <button onClick={() => setOpen(false)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"><MdClose className="w-4 h-4" /></button>
                    </div>
                </div>

                {activeSession && remoteError && (
                    <div className="mx-3 mt-2 rounded border border-red-500/30 bg-red-500/10 px-2 py-1 text-[10px] text-red-300">
                        {remoteError}
                    </div>
                )}

                {activeSession && remoteStatus && (
                    <div className="px-3 py-1 text-[10px] text-white/70 border-b border-white/10">
                        {remoteStatus.customerOnline ? '🟢 Customer online' : '⚫ Customer offline — remote access unavailable'}
                        {remoteStatus.remoteSessionStatus ? ` · ${remoteStatus.remoteSessionStatus}` : ''}
                    </div>
                )}

                {!activeSession ? (
                    <div className="flex-1 overflow-y-auto bg-[#0088cc]/5">
                        {sessions.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-[#0088cc]/65 px-8 text-center gap-2">
                                <MdChatBubbleOutline className="w-10 h-10 text-[#0088cc]/45" />
                                <p className="text-sm font-semibold">No active chats</p>
                                <p className="text-xs">{wsReady ? 'Online — technical issue requests from customers and vendors will appear here.' : 'Connecting...'}</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-[#0088cc]/15">
                                {sessions.map(s => (
                                    <button key={s.sessionId} onClick={() => joinSession(s)}
                                        className="w-full text-left px-4 py-3 hover:bg-white transition-colors flex items-start gap-3">
                                        <div className="w-9 h-9 rounded-full bg-[#0088cc] flex items-center justify-center text-white font-bold text-sm shrink-0 mt-0.5">
                                            {sessionInitial(s.clientName, s.clientEmail)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="font-semibold text-[13px] text-[hsl(var(--foreground))] truncate">{s.clientName}</p>
                                                <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0",
                                                    s.status === 'waiting' ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700")}>
                                                    {s.status === 'waiting' ? 'Waiting' : 'Active'}
                                                </span>
                                            </div>
                                            <p className="text-[11px] text-[#0088cc]/65 truncate">{s.clientEmail} · {s.clientPortal} portal</p>
                                            {s.messages.length > 0 && (
                                                <p className="text-[12px] text-[hsl(var(--muted-foreground))] mt-0.5 truncate">
                                                    {s.messages[s.messages.length - 1].text}
                                                </p>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col overflow-hidden bg-[hsl(var(--background))]">
                        {activeSession.alreadyHandled && (
                            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs font-semibold text-center">
                                🔒 This chat is handled by <strong>{activeSession.agentName}</strong>. Read-only view.
                            </div>
                        )}
                        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 flex flex-col">
                            {(activeSession.messages ?? []).map((m, i) => {
                                if (m.sender === 'system') return <p key={i} className="text-center text-[10px] text-[#0088cc]/65 italic py-0.5">{m.text}</p>;
                                const self = m.sender === 'agent';
                                return (
                                    <div key={i} className={cn("max-w-[78%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed",
                                        self ? "bg-[#0f172a] text-white self-end rounded-tr-none" : "bg-[#0088cc]/10 text-[hsl(var(--foreground))] self-start rounded-tl-none border border-[#0088cc]/20")}>
                                        {!self && <p className="text-[10px] font-bold text-[hsl(var(--muted-foreground))] mb-0.5">{m.senderName}</p>}
                                        <p className="whitespace-pre-wrap">{m.text}</p>
                                        <span className={cn("text-[9px] block text-right mt-0.5", self ? "text-white/60" : "text-[#0088cc]/65")}>
                                            {new Date(m.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                );
                            })}
                            <div ref={endRef} />
                        </div>
                        {activeSession.status !== 'closed' && !activeSession.alreadyHandled && (
                            <div className="shrink-0 px-3 pb-3 pt-2 border-t border-[#0088cc]/15">
                                <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl flex items-end px-3 py-1.5 focus-within:border-[#0088cc] focus-within:ring-1 focus-within:ring-[#0088cc]/30 transition-all">
                                    <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
                                        placeholder="Reply to customer/vendor..."
                                        className="flex-1 bg-transparent border-none outline-none resize-none text-[13px] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] min-h-[22px] max-h-[80px] py-0.5 mr-2" rows={1} />
                                    <button onClick={sendMsg} disabled={!input.trim()}
                                        className={cn("w-8 h-8 rounded-full flex items-center justify-center transition-all shrink-0",
                                            input.trim() ? "bg-[#0f172a] text-white hover:bg-slate-800 active:scale-95" : "bg-[#0088cc]/10 text-[#0088cc]/45")}>
                                        <MdSend className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                        {activeSession.alreadyHandled && (
                            <div className="shrink-0 px-3 pb-3 pt-2 border-t border-[#0088cc]/15 text-center text-[11px] text-[#0088cc]/65">
                                You are observing this chat. Only {activeSession.agentName} can reply.
                            </div>
                        )}
                    </div>
                )}
            </div>

            <button onClick={() => { setOpen(o => !o); if (!open) { setUnread(0); stopRing(); } }}
                className="w-14 h-14 bg-[#0f172a] hover:bg-slate-800 text-white rounded-full flex items-center justify-center shadow-xl transition-all hover:scale-105 active:scale-95 pointer-events-auto">
                {open ? <MdKeyboardArrowDown className="w-7 h-7" /> : (
                    <div className="relative">
                        <MdChatBubbleOutline className="w-6 h-6" />
                        {unread > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 border-2 border-[#0f172a] flex items-center justify-center text-[9px] font-bold">
                                {unread > 9 ? '9+' : unread}
                            </span>
                        )}
                    </div>
                )}
            </button>

            {showRemoteModal && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 pointer-events-auto">
                    <div className="w-full max-w-sm rounded-xl border border-slate-700 bg-slate-950 p-5 shadow-2xl space-y-3 text-slate-100">
                        <h3 className="text-base font-bold">Request Remote Support</h3>
                        <p className="text-[11px] text-slate-400">
                            Customer receives an approval popup with permissions and estimated session time.
                        </p>
                        <label className="block text-xs space-y-1">
                            <span className="text-slate-400">Purpose</span>
                            <input
                                value={remotePurpose}
                                onChange={(e) => setRemotePurpose(e.target.value)}
                                placeholder="e.g. Troubleshoot RFQ approval issue"
                                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
                            />
                        </label>
                        <label className="block text-xs space-y-1">
                            <span className="text-slate-400">Estimated duration (minutes)</span>
                            <input
                                type="number"
                                min={5}
                                max={120}
                                value={remoteDuration}
                                onChange={(e) => setRemoteDuration(Number(e.target.value))}
                                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
                            />
                        </label>

                        <div className="flex gap-2 pt-1">
                            <button
                                type="button"
                                onClick={() => setShowRemoteModal(false)}
                                className="flex-1 rounded-lg border border-slate-700 py-2 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={remoteLoading}
                                onClick={handleRequestRemote}
                                className="flex-1 rounded-lg bg-emerald-600 py-2 text-sm font-medium text-white disabled:opacity-50"
                            >
                                {remoteLoading ? 'Sending…' : 'Send Request'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
